import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { BidflowMetadata } from '../lib/bidflowMetadata';
import { saveAnnotationSnapshotV2 } from '../lib/annotationV2Persistence';

export interface Project {
  id: string;
  name: string;
  folderId: string; // Reference to folder
  clientId: string; // Reference to client
  clientName: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  projectType: string;
  status: string;
  estimatedBudget?: number;
  contractSize?: number;
  startDate?: string;
  endDate?: string;
  description?: string;
  bidDeadline?: string;
  files?: Array<{
    id: string;
    name: string;
    size: number;
    type: string;
    storagePath: string;
    url?: string;
  }>;
  specs?: Array<{
    id: string;
    name: string;
    size: number;
    type: string;
    storagePath: string;
    url?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt?: string;
  fromSupabase?: boolean;
}

export interface Activity {
  id: string;
  type: 'created' | 'completed' | 'budget' | 'renamed' | 'deleted' | 'status' | 'file-added' | 'file-removed';
  text: string;
  project: string;
  projectId?: string;
  time: string;
  timestamp: string;
}

interface ProjectsContextType {
  projects: Project[];
  activities: Activity[];
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  addProject: (project: Project, bidflowMetadata?: BidflowMetadata) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  addFileActivity: (projectId: string, fileName: string, type: 'added' | 'removed') => void;
  searchProjects: (query: string) => Project[];
  getProjectsByFolder: (folderId: string) => Project[];
  getAllProjectsInFolderTree: (folderId: string, folders: any[]) => Project[];
  stats: {
    total: number;
    inProgress: number;
    completed: number;
    planning: number;
    review: number;
    totalBudget: number;
  };
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);

  // Fetch activities from Supabase
  const fetchActivities = useCallback(async () => {
    if (!supabase) return;

    const { data, error } = await supabase
      .from('project_activities')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching activities:', error);
      return;
    }

    if (data) {
      const formattedActivities: Activity[] = data.map((a: any) => ({
        id: a.id,
        type: a.activity_type,
        text: a.activity_text,
        project: a.project_name,
        projectId: a.project_id,
        time: 'Just now', // Will be computed on render
        timestamp: a.created_at,
      }));
      setActivities(formattedActivities);
    }
  }, []);

  useEffect(() => {
    if (!supabase) return;

    fetchActivities();

    // Subscribe to activity changes
    const subscription = supabase
      .channel('activities-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'project_activities' }, () => {
        fetchActivities();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchActivities]);

  const addActivity = async (activity: Omit<Activity, 'id' | 'timestamp'>) => {
    if (supabase) {
      // Save to Supabase
      const { error } = await supabase
        .from('project_activities')
        .insert({
          project_id: activity.projectId || null,
          project_name: activity.project,
          activity_type: activity.type,
          activity_text: activity.text,
        });

      if (error) {
        console.error('Error saving activity:', error);
      } else {
        // Manually refetch activities after a short delay to ensure the insert is committed
        setTimeout(() => {
          fetchActivities();
        }, 500);
      }
    }
  };

  const addProject = async (project: Project, bidflowMetadata?: BidflowMetadata) => {
    if (supabase) {
      // Get the logged-in username
      const username = localStorage.getItem('bidflow_username');
      
      // Add to estimations table
      const { data, error} = await supabase
        .from('estimations')
        .insert([{
          name: project.name,
          folder_id: project.folderId,
          client_id: project.clientId,
          client_name: project.clientName,
          description: project.description,
          bid_deadline: project.bidDeadline,
          status: project.status,
          project_type: project.projectType,
          estimated_budget: project.estimatedBudget,
          username: username,
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding estimation to Supabase:', error);
        return;
      }

      if (data) {
        // If there are files, add them to estimation_files table and collect IDs
        let insertedFileIds: { id: string; file_name: string }[] = [];
        if (project.files && project.files.length > 0) {
          const fileInserts = project.files.map((file, index) => ({
            estimation_id: data.id,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            storage_path: file.storagePath,
            file_order: index,
          }));

          const { data: filesData, error: filesError } = await supabase
            .from('estimation_files')
            .insert(fileInserts)
            .select('id, file_name');

          if (filesError) {
            console.error('Error adding estimation files:', filesError);
          } else if (filesData) {
            insertedFileIds = filesData;
          }
        }

        // If there are specs, add them to estimation_specs table
        if (project.specs && project.specs.length > 0) {
          const specInserts = project.specs.map((spec, index) => ({
            estimation_id: data.id,
            file_name: spec.name,
            file_size: spec.size,
            file_type: spec.type,
            storage_path: spec.storagePath,
            file_order: index,
          }));

          const { error: specsError } = await supabase
            .from('estimation_specs')
            .insert(specInserts);

          if (specsError) {
            console.error('Error adding estimation specs:', specsError);
          }
        }

        // Seed from bidflow metadata if present
        if (bidflowMetadata) {
          await seedProjectFromMetadata(data.id, bidflowMetadata, insertedFileIds);
        }

        const newProject: Project = {
          ...project,
          id: data.id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          fromSupabase: true,
        };
        setProjects(prev => [...prev, newProject]);
        addActivity({
          type: 'created',
          text: 'created',
          project: newProject.name,
          projectId: newProject.id,
          time: 'Just now',
        });
      }
    } else {
      // Fallback to local storage
      setProjects(prev => [...prev, project]);
      addActivity({
        type: 'created',
        text: 'created',
        project: project.name,
        projectId: project.id,
        time: 'Just now',
      });
    }
  };

  /**
   * Seed a newly created project with data from embedded bidflow metadata.
   * Sets project_key, inserts annotations, and stores reference images.
   */
  const seedProjectFromMetadata = async (
    estimationId: string,
    metadata: BidflowMetadata,
    insertedFiles: { id: string; file_name: string }[]
  ) => {
    if (!supabase) return;

    try {
      console.log('🌱 Seeding project from bidflow metadata...');

      // 1. Merge reference images into project_key components and set on estimation
      const keyWithImages = {
        ...metadata.projectKey,
        components: metadata.projectKey.components.map(comp => ({
          ...comp,
          referenceImage: metadata.referenceImages[comp.name] || '',
          hasReferenceImage: !!metadata.referenceImages[comp.name],
        })),
      };

      const { error: keyError } = await supabase
        .from('estimations')
        .update({ project_key: keyWithImages })
        .eq('id', estimationId);

      if (keyError) {
        console.error('Error seeding project_key:', keyError);
      } else {
        console.log(`✅ Seeded project_key with ${keyWithImages.components.length} components`);
      }

      // 2. Insert annotations — match file by name to get the DB file_id
      const fileByName = new Map(insertedFiles.map(f => [f.file_name, f.id]));
      const targetFileId = metadata.fileName ? fileByName.get(metadata.fileName) : insertedFiles[0]?.id;

      const snapshotAnnotations = Object.entries(metadata.annotations).flatMap(([componentType, anns]) => {
        if (!Array.isArray(anns)) return [];
        return anns.map((ann: any) => ({
          ...ann,
          component_type: ann?.component_type || componentType,
        }));
      });

      if (snapshotAnnotations.length > 0) {
        const { changedComponentTypes } = await saveAnnotationSnapshotV2({
          supabase: supabase as any,
          estimationId,
          activeMode: targetFileId ? 'draw' : 'estimate',
          currentDrawingFileId: targetFileId || null,
          annotations: snapshotAnnotations,
        });
        console.log(`✅ Seeded annotation_entities_v2 for ${changedComponentTypes.length} component types`);
      }

      const totalAnnotations = Object.values(metadata.annotations).reduce((sum, arr) => sum + arr.length, 0);
      console.log(`✅ Seeded ${totalAnnotations} annotations across ${Object.keys(metadata.annotations).length} component types`);

    } catch (err) {
      console.error('Error seeding project from metadata:', err);
    }
  };

  const updateProject = async (id: string, updates: Partial<Project>) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;

    if (supabase) {
      // Convert camelCase to snake_case for Supabase
      const supabaseUpdates: any = {};
      if (updates.status) supabaseUpdates.status = updates.status;
      if (updates.folderId) supabaseUpdates.folder_id = updates.folderId;
      if (updates.clientId) supabaseUpdates.client_id = updates.clientId;
      if (updates.clientName) supabaseUpdates.client_name = updates.clientName;
      if (updates.description !== undefined) supabaseUpdates.description = updates.description;
      if (updates.bidDeadline !== undefined) supabaseUpdates.bid_deadline = updates.bidDeadline;
      if (updates.estimatedBudget !== undefined) supabaseUpdates.estimated_budget = updates.estimatedBudget;
      if (updates.contractSize !== undefined) supabaseUpdates.contract_size = updates.contractSize;
      if (updates.name) supabaseUpdates.name = updates.name;
      
      const { error } = await supabase
        .from('estimations')
        .update(supabaseUpdates)
        .eq('id', id);

      if (error) {
        console.error('Error updating estimation in Supabase:', error);
        return;
      }

      // Handle file updates
      if (updates.files !== undefined) {
        // Delete existing files from estimation_files
        const { error: deleteError } = await supabase
          .from('estimation_files')
          .delete()
          .eq('estimation_id', id);

        if (deleteError) {
          console.error('Error deleting existing files:', deleteError);
        }

        // Insert new files
        if (updates.files && updates.files.length > 0) {
          const fileInserts = updates.files.map((file, index) => ({
            estimation_id: id,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            storage_path: file.storagePath,
            file_order: index,
          }));

          const { error: insertError } = await supabase
            .from('estimation_files')
            .insert(fileInserts);

          if (insertError) {
            console.error('Error inserting updated files:', insertError);
          }
        }
      }

      // Handle spec updates
      if (updates.specs !== undefined) {
        // Delete existing specs from estimation_specs
        const { error: deleteError } = await supabase
          .from('estimation_specs')
          .delete()
          .eq('estimation_id', id);

        if (deleteError) {
          console.error('Error deleting existing specs:', deleteError);
        }

        // Insert new specs
        if (updates.specs && updates.specs.length > 0) {
          const specInserts = updates.specs.map((spec, index) => ({
            estimation_id: id,
            file_name: spec.name,
            file_size: spec.size,
            file_type: spec.type,
            storage_path: spec.storagePath,
            file_order: index,
          }));

          const { error: insertError } = await supabase
            .from('estimation_specs')
            .insert(specInserts);

          if (insertError) {
            console.error('Error inserting updated specs:', insertError);
          }
        }
      }
    }

    // Track specific types of updates
    if (updates.name && updates.name !== project.name) {
      addActivity({
        type: 'renamed',
        text: `Renamed from "${project.name}" to`,
        project: updates.name,
        projectId: id,
        time: 'Just now',
      });
    }
    
    if (updates.status && updates.status !== project.status) {
      if (updates.status === 'accepted') {
        const contractAmount = updates.contractSize || 0;
        addActivity({
          type: 'completed',
          text: `marked as completed${contractAmount > 0 ? ` with contract size $${contractAmount.toLocaleString()}` : ''}`,
          project: project.name,
          projectId: id,
          time: 'Just now',
        });
      } else if (updates.status === 'proposal') {
        const estimateAmount = updates.estimatedBudget !== undefined ? updates.estimatedBudget : 0;
        addActivity({
          type: 'status',
          text: `status changed to Proposal${estimateAmount >= 0 ? ` with estimate $${estimateAmount.toLocaleString()}` : ''}`,
          project: project.name,
          projectId: id,
          time: 'Just now',
        });
      } else {
        const statusLabels: { [key: string]: string } = {
          'estimating': 'Estimating',
          'proposal': 'Proposal',
          'in-review': 'In Review',
          'accepted': 'Accepted',
          'rejected': 'Rejected',
        };
        addActivity({
          type: 'status',
          text: `status changed to ${statusLabels[updates.status] || updates.status}`,
          project: project.name,
          projectId: id,
          time: 'Just now',
        });
      }
    }
    
    if (updates.estimatedBudget !== undefined && updates.estimatedBudget !== project.estimatedBudget) {
      addActivity({
        type: 'budget',
        text: 'Budget updated for',
        project: project.name,
        projectId: id,
        time: 'Just now',
      });
    }
    
    if (updates.bidDeadline !== undefined && updates.bidDeadline !== project.bidDeadline) {
      addActivity({
        type: 'status',
        text: `Bid deadline ${updates.bidDeadline ? 'updated to ' + updates.bidDeadline : 'removed for'}`,
        project: project.name,
        projectId: id,
        time: 'Just now',
      });
    }
    
    if (updates.description !== undefined && updates.description !== project.description) {
      addActivity({
        type: 'status',
        text: updates.description ? 'description updated' : 'description removed',
        project: project.name,
        projectId: id,
        time: 'Just now',
      });
    }

    setProjects(prev => prev.map(p => 
      p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
    ));
  };

  const deleteProject = async (id: string) => {
    const project = projects.find(p => p.id === id);
    const projectName = project?.name || 'Unknown Project';

    if (supabase) {
      // Delete from Supabase (will cascade delete estimation_files due to ON DELETE CASCADE)
      const { error } = await supabase
        .from('estimations')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting estimation from Supabase:', error);
        return;
      }
      
      // Also delete files from storage
      if (project?.files) {
        for (const file of project.files) {
          await supabase.storage
            .from('bucket')
            .remove([file.storagePath]);
        }
      }
      
      // Also delete specs from storage
      if (project?.specs) {
        for (const spec of project.specs) {
          await supabase.storage
            .from('bucket')
            .remove([spec.storagePath]);
        }
      }
    }

    addActivity({
      type: 'deleted',
      text: 'deleted',
      project: projectName,
      time: 'Just now',
    });

    setProjects(prev => prev.filter(p => p.id !== id));
  };

  const searchProjects = (query: string) => {
    const lowerQuery = query.toLowerCase();
    return projects.filter(p => 
      p.name.toLowerCase().includes(lowerQuery) ||
      p.clientName.toLowerCase().includes(lowerQuery) ||
      p.address.toLowerCase().includes(lowerQuery) ||
      p.city.toLowerCase().includes(lowerQuery)
    );
  };

  const getProjectsByFolder = (folderId: string) => {
    return projects.filter(p => p.folderId === folderId);
  };

  const getAllProjectsInFolderTree = (folderId: string, folders: any[]) => {
    // Get all subfolder IDs recursively
    const getSubfolderIds = (id: string): string[] => {
      const subfolders = folders.filter(f => f.parentId === id);
      const ids = [id];
      subfolders.forEach(sf => {
        ids.push(...getSubfolderIds(sf.id));
      });
      return ids;
    };

    const allFolderIds = getSubfolderIds(folderId);
    return projects.filter(p => allFolderIds.includes(p.folderId));
  };

  const addFileActivity = (projectId: string, fileName: string, type: 'added' | 'removed') => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (type === 'added') {
      addActivity({
        type: 'file-added',
        text: `file "${fileName}" added to`,
        project: project.name,
        projectId: projectId,
        time: 'Just now',
      });
    } else {
      addActivity({
        type: 'file-removed',
        text: `file "${fileName}" removed from`,
        project: project.name,
        projectId: projectId,
        time: 'Just now',
      });
    }
  };

  const acceptedProjects = projects.filter(p => p.status === 'accepted');
  const rejectedProjects = projects.filter(p => p.status === 'rejected');
  const totalWithOutcome = acceptedProjects.length + rejectedProjects.length;

  const stats = {
    total: projects.length,
    inProgress: projects.filter(p => p.status !== 'accepted' && p.status !== 'rejected').length,
    completed: acceptedProjects.length,
    planning: projects.filter(p => p.status === 'estimating').length,
    review: projects.filter(p => p.status === 'in-review').length,
    totalBudget: acceptedProjects.reduce((sum, p) => sum + (p.contractSize || p.estimatedBudget || 0), 0),
  };

  return (
    <ProjectsContext.Provider value={{
      projects,
      activities,
      setProjects,
      addProject,
      updateProject,
      deleteProject,
      addFileActivity,
      searchProjects,
      getProjectsByFolder,
      getAllProjectsInFolderTree,
      stats,
    }}>
      {children}
    </ProjectsContext.Provider>
  );
}

export function useProjects() {
  const context = useContext(ProjectsContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }
  return context;
}
